import { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { SortOption } from '../utils/studentFilters';

interface SortMenuProps {
  sortBy: SortOption;
  sortDirection: 'asc' | 'desc';
  onSortChange: (sortBy: SortOption, sortDirection: 'asc' | 'desc') => void;
}

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'waitTime', label: 'Wait Time' },
  { value: 'checkedIn', label: 'Check-in Time' },
  { value: 'name', label: 'Name' },
  { value: 'grade', label: 'Grade' },
];

export default function SortMenu({ sortBy, sortDirection, onSortChange }: SortMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSortOptionClick = (option: SortOption) => {
    // If clicking the same option, toggle direction
    if (option === sortBy) {
      onSortChange(option, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default direction for new option
      const defaultDirection = option === 'name' || option === 'grade' ? 'asc' : 'desc';
      onSortChange(option, defaultDirection);
    }
    handleClose();
  };

  const currentLabel = sortOptions.find(opt => opt.value === sortBy)?.label || 'Sort';

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<SortIcon />}
        endIcon={sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        onClick={handleClick}
        sx={{ textTransform: 'none' }}
      >
        {currentLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {sortOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleSortOptionClick(option.value)}
            selected={sortBy === option.value}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {sortBy === option.value && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
